<template>
    <div class="integration-form">
        <b-card>
            <h2>{{ id ? $t('room_availability.edit') : $t('room_availability.add') }}</h2>

            <b-form @submit.prevent="handleSubmit" @reset="resetForm" novalidate>
                <!-- Room Selection -->
                <div class="mb-3 text-left">
                    <label class="mb-1" for="room_id">{{ $t('room_availability.room_id') }}</label>
                    <treeselect :placeholder="$t('room_availability.room_id')" v-model="formFields.room_id"
                        :options="roomOptions" class="form-input"
                        :class="formErrors.has('room_id') ? 'error-input' : ''" @input="removeError('room_id')" />
                    <b-form-invalid-feedback :state="!formErrors.has('room_id')">
                        {{ formErrors.first('room_id') }}
                    </b-form-invalid-feedback>
                </div>

                <!-- Date Picker -->
                <div class="mb-3 text-left">
                    <label class="mb-1" for="date">{{ $t('room_availability.date') }}</label>
                    <b-form-datepicker id="date_picker" v-model="formFields.date" reset-button today-button locale="en"
                        class="w-100" />
                    <b-form-invalid-feedback :state="!formErrors.has('date')">
                        {{ formErrors.first('date') }}
                    </b-form-invalid-feedback>
                </div>

                <!-- Availability Status -->
                <b-form-group :label="$t('room_availability.status')" class="mb-1">
                    <b-button-group size="md">
                        <b-button :variant="formFields.is_available === 1 ? 'success' : 'outline-secondary'"
                            @click="formFields.is_available = 1">
                            {{ $t('room_availability.available') }}
                        </b-button>
                        <b-button :variant="formFields.is_available === 0 ? 'danger' : 'outline-secondary'"
                            @click="formFields.is_available = 0">
                            {{ $t('room_availability.unavailable') }}
                        </b-button>
                    </b-button-group>
                </b-form-group>

                <!-- Override Price -->
                <div class="mb-3 text-left">
                    <label class="mb-1" for="override_price">{{ $t('room_availability.override_price') }}</label>
                    <b-form-input id="override_price" type="number" min="0" v-model="formFields.override_price"
                        :placeholder="$t('room_availability.override_price')" />
                    <b-form-invalid-feedback :state="!formErrors.has('override_price')">
                        {{ formErrors.first('override_price') }}
                    </b-form-invalid-feedback>
                </div>

                <!-- Action Buttons -->
                <div class="d-flex gap-2 mt-4">
                    <b-button type="submit" variant="primary" :disabled="isSubmitting">
                        <b-spinner small v-if="isSubmitting" class="mr-1" />
                        {{ $t('action.submit') }}
                    </b-button>
                    <b-button variant="secondary" @click="cancel">
                        {{ $t('action.cancel') }}
                    </b-button>
                </div>
            </b-form>
        </b-card>
    </div>
</template>

<script>
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import { request } from '@/Util/Request';
import Error from '@/Util/Error';

const FORM_STATE = {
    date: null,
    room_id: null,
    is_available: 1,
    override_price: 0,
};

export default {
    name: 'RoomAvailabilityForm',
    components: { Treeselect },
    data() {
        return {
            id: null,
            formFields: { ...FORM_STATE },
            formErrors: new Error({}),
            validated: false,
            isSubmitting: false,
            roomOptions: [],
        };
    },
    mounted() {
        this.id = this.$route.params.id || null;
        this.fetchRooms().then(() => {
            if (this.id) this.loadRoomAvailability();
        });
    },
    methods: {
        async fetchRooms() {
            try {
                const response = await request({ method: 'get', url: '/get-hotel-users' });

                this.roomOptions = response.data.map((room) => ({
                    id: String(room.id),
                    label: `${room.hotel.name} | Room ${room.room_number} - ${room.room_type.name}, ${room.bed_size.name} bed`,
                }));
            } catch (error) {
                console.error('Failed to load Rooms', error);
            }
        },
        async loadRoomAvailability() {
            try {
                const response = await request({ method: 'get', url: `/room-availabilites/${this.id}` });
                console.log('Room Availability Response:', response.data);
                
                this.formFields = {
                    date: response.data.date,
                    room_id: String(response.data.rooms.id),
                    is_available: response.data.is_available,
                    override_price: response.data.override_price,
                };
            } catch (error) {
                console.error('Failed to load availability', error);
            }
        },
        async handleSubmit() {
            try {
                this.formErrors = new Error({});
                this.isSubmitting = true;

                const method = this.id ? 'put' : 'post';
                const url = this.id ? `/room-availabilites/${this.id}` : '/room-availabilites';

                await request({ method, url, data: this.formFields });

                this.$bvToast.toast(
                    this.$t(
                        this.id
                            ? 'room_availability.updated_successfully'
                            : 'room_availability.created_successfully'
                    ),
                    {
                        title: this.$t('common.success'),
                        variant: 'success',
                        solid: true,
                    }
                );

                this.$router.push(`/admin/room-availabilites`);
            } catch (error) {
                if (error.request && error.request.status && error.request.status === 422) {
                    this.formErrors = new Error(JSON.parse(error.request.responseText).errors);
                    return false;
                } else {
                    this.notifyError();
                }
            } finally {
                this.isSubmitting = false;
            }
        },
        removeError(key) {
            Array.isArray(key) ? key.forEach((k) => this.formErrors.remove(k)) : this.formErrors.remove(key);
        },
        cancel() {
            this.$router.back();
        },
        resetForm() {
            this.formFields = { ...FORM_STATE };
            this.formErrors = new Error({});
            this.validated = false;
        },
    },
};
</script>
